import { useNavigate, useSearch } from "@tanstack/react-router";
import { Check, Flag, History, Lock } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { BulkActionBar } from "@/components/cinch/BulkActionBar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DataTable,
  type DataTableColumn,
  type DataTableSort,
} from "@/components/cinch/DataTable/DataTable";
import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { IconButton } from "@/components/cinch/IconButton";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Card } from "@/components/ui/card";
import { TableCell, TableRow } from "@/components/ui/table";

import type { ReconStepSearch } from "..";
import {
  monthLabel,
  useReviewSignedContract,
  useSignedContracts,
  type ReviewStatus,
  type SignedContract,
} from "../api";
import { fmtCur, fmtCur2, fmtDate } from "../../renewals/format";
import { SignedContractDrawer } from "./SignedContractDrawer";

const REVIEW_LABELS: { value: ReviewStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "flagged", label: "Flagged" },
];

/** R-14 worklist: every contract signed in the close month, read LIVE from
 *  Cinch, with a per-contract verdict.
 *
 *  No pull button on purpose. The step's question is "is the record complete
 *  right now?", so a contract fixed in Cinch stops looking wrong immediately
 *  and one entered late simply appears. The audit copy is frozen when the step
 *  is marked complete — that being the one moment it means anything. An open
 *  flag blocks that completion. */
export function SignedContractsPanel({ period }: { period: string }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ReconStepSearch;
  const setSearch = (next: Partial<ReconStepSearch>) =>
    navigate({
      to: "/reconciliation/$period/$stepKey",
      params: { period, stepKey: "R-14" },
      search: (prev: ReconStepSearch) => ({ ...prev, ...next }),
      replace: true,
    });

  const { data, isLoading, error } = useSignedContracts(period);
  const review = useReviewSignedContract(period);
  const [sort, setSort] = useState<DataTableSort | null>({
    key: "sign_date",
    dir: "desc",
  });
  // Whether the drawer was opened via a Flag button (preselects the verdict).
  const [flagIntent, setFlagIntent] = useState(false);

  const contracts = useMemo(() => data?.contracts ?? [], [data]);
  const reviewFilter = useMemo(
    () => (search.review ?? "").split(",").filter(Boolean) as ReviewStatus[],
    [search.review],
  );
  const q = search.q ?? "";

  const rows = useMemo(() => {
    let out = contracts;
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter((c) =>
        `${c.company_name ?? ""} ${c.company_dba ?? ""} ${c.contract_type_name ?? ""} ${c.closer_name ?? ""}`
          .toLowerCase()
          .includes(needle),
      );
    }
    if (reviewFilter.length) {
      out = out.filter((c) => reviewFilter.includes(c.review_status));
    }
    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      out = [...out].sort((a, b) => {
        const va = value(a, sort.key);
        const vb = value(b, sort.key);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        return va < vb ? -dir : va > vb ? dir : 0;
      });
    }
    return out;
  }, [contracts, q, reviewFilter, sort]);

  const shownMrr = rows.reduce((s, c) => s + (c.mrr_total ?? 0), 0);
  const summary = data?.summary;
  const locked = data?.locked ?? false;
  const canAct = (data?.can_act ?? false) && !locked;
  const supersededCount = contracts.filter((c) => c.review_superseded).length;
  const curLabel = monthLabel(period);
  const drawerContract = useMemo(
    () => contracts.find((c) => c.id === search.contract) ?? null,
    [contracts, search.contract],
  );

  function quickReview(c: SignedContract, status: ReviewStatus) {
    review.mutate(
      { contractId: c.id, status },
      {
        onError: (e) =>
          toast.error("Couldn't save that review", {
            description: (e as Error).message,
          }),
      },
    );
  }

  /** Two ways in, two sizes, on purpose:
   *
   *  - a row (or the flag button) opens the QUICK VIEW — you're judging the
   *    numbers already on the row and recording a verdict;
   *  - the company name opens EXPANDED — you're going to read the contract
   *    itself, and line items in a 480px drawer is a squint.
   *
   *  `expanded` stays in the URL either way, so the size is shareable and the
   *  chevron still overrides whichever you came in through. */
  function openDrawer(c: SignedContract, flag: boolean, expanded = false) {
    setFlagIntent(flag);
    setSearch({ contract: c.id, expanded: expanded || undefined });
  }

  const columns: DataTableColumn<SignedContract>[] = [
    {
      key: "company_name",
      header: "Company",
      sortable: true,
      className: "max-w-[240px]",
      render: (c) => (
        <button
          type="button"
          className="block max-w-full truncate text-left text-sm font-semibold text-brand hover:underline"
          title={`${c.company_name ?? "this contract"} — open the full contract: line items, PandaDoc, HubSpot deal`}
          onClick={(e) => {
            e.stopPropagation();
            openDrawer(c, false, true);
          }}
        >
          {c.company_name ?? "—"}
        </button>
      ),
    },
    {
      key: "company_dba",
      header: "DBA",
      sortable: true,
      className: "max-w-[160px]",
      render: (c) => (
        <span className="block truncate" title={c.company_dba ?? undefined}>
          {c.company_dba ?? "—"}
        </span>
      ),
    },
    {
      key: "contract_type_name",
      header: "Contract Type",
      sortable: true,
      render: (c) => c.contract_type_name ?? "—",
    },
    {
      key: "mrr_total",
      header: "MRR",
      numeric: true,
      sortable: true,
      render: (c) => fmtCur(c.mrr_total),
    },
    {
      key: "arr",
      header: "ARR",
      numeric: true,
      render: (c) => fmtCur((c.mrr_total ?? 0) * 12),
    },
    {
      key: "term_months",
      header: "Term",
      sortable: true,
      render: (c) => (c.term_months ? `${c.term_months} months` : "—"),
    },
    {
      key: "payment_type",
      header: "Payment",
      sortable: true,
      render: (c) => c.payment_type ?? "—",
    },
    {
      key: "sign_date",
      header: "Sign Date",
      sortable: true,
      render: (c) => fmtDate(c.sign_date),
    },
    {
      key: "billing_effective_from",
      header: "Billing Effective",
      sortable: true,
      render: (c) =>
        c.billing_effective_from ? (
          fmtDate(c.billing_effective_from)
        ) : (
          <span
            className="font-medium text-warning"
            title="No billing effective date — will surface on the billing-start worklist"
          >
            missing
          </span>
        ),
    },
    {
      key: "closer_name",
      header: "Closer",
      sortable: true,
      render: (c) => c.closer_name ?? "—",
    },
    {
      key: "review",
      header: "Review",
      sortable: true,
      align: "right",
      className: "w-40",
      render: (c) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          {c.review_superseded && (
            <History
              className="h-3.5 w-3.5 text-warning"
              aria-label="Review lapsed — the contract changed after it was reviewed"
            />
          )}
          <StatusBadge value={c.review_status} />
          {canAct && (
            <>
              <IconButton
                title="Approve — this contract is entered correctly"
                active={c.review_status === "approved"}
                activeClassName="text-success"
                hoverClassName="hover:bg-success-light hover:text-success"
                disabled={review.isPending}
                onClick={() => quickReview(c, "approved")}
              >
                <Check className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                title="Flag — something's wrong (opens notes); blocks completing this step"
                active={c.review_status === "flagged"}
                activeClassName="text-warning"
                hoverClassName="hover:bg-warning-light hover:text-warning"
                onClick={() => openDrawer(c, true)}
              >
                <Flag className="h-3.5 w-3.5" />
              </IconButton>
            </>
          )}
        </span>
      ),
    },
  ];

  if (isLoading && !data) return <Card className="h-64 animate-pulse" />;

  return (
    <div className="flex flex-col gap-4">
      {data?.error && (
        <Alert variant="danger">
          <AlertTitle>Couldn't reach the Cinch API</AlertTitle>
          <AlertDescription>{data.error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {locked && data?.frozen_at
            ? `Frozen ${new Date(data.frozen_at).toLocaleString()} when the step was signed off`
            : "Live from Cinch — this list is the contract record as it stands right now."}
        </div>
        {locked && (
          <span className="flex items-center gap-1 text-xs font-medium text-success">
            <Lock className="h-3.5 w-3.5" /> Signed off — frozen
          </span>
        )}
      </div>

      {supersededCount > 0 && (
        <Alert variant="warning">
          <AlertTitle>
            {supersededCount} review{supersededCount === 1 ? "" : "s"} lapsed
          </AlertTitle>
          <AlertDescription>
            {supersededCount === 1 ? "A contract was" : "Contracts were"} changed
            in Cinch after being reviewed, so the earlier verdict no longer
            describes {supersededCount === 1 ? "it" : "them"}. The notes were
            kept — re-review to clear.
          </AlertDescription>
        </Alert>
      )}

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}
      >
        <KpiTile
          label={`Contracts signed · ${curLabel}`}
          value={String(data?.contract_count ?? 0)}
          sub={locked ? "frozen at sign-off" : "live from Cinch"}
        />
        <KpiTile
          label="Total MRR signed"
          value={fmtCur2(data?.mrr_total ?? 0)}
          sub={`${fmtCur(data?.arr_total ?? 0)} annualized`}
        />
        <KpiTile
          label="Missing billing effective"
          value={String(summary?.missing_effective_count ?? 0)}
          tone={summary?.missing_effective_count ? "warning" : "positive"}
          sub="handled in the billing-start step"
        />
        <KpiTile
          label="Awaiting review"
          value={String(summary?.pending_count ?? 0)}
          tone={summary?.pending_count ? "warning" : "positive"}
        />
        <KpiTile
          label="Flagged"
          value={String(summary?.flagged_count ?? 0)}
          tone={summary?.flagged_count ? "danger" : "positive"}
          info="An open flag blocks completing this step — completing it is the attestation that every contract signed this month is entered correctly."
          onClick={() =>
            setSearch({ review: search.review === "flagged" ? undefined : "flagged" })
          }
          selected={search.review === "flagged"}
        />
      </div>

      <FilterBar>
        <FilterSearch
          placeholder="Search company, type, closer…"
          value={q}
          onChange={(v) => setSearch({ q: v || undefined })}
        />
        <FilterChip label="Review" count={reviewFilter.length}>
          <CheckboxList
            options={REVIEW_LABELS}
            selected={reviewFilter}
            onChange={(v) => setSearch({ review: v.length ? v.join(",") : undefined })}
          />
        </FilterChip>
        {reviewFilter.map((r) => (
          <ActiveFilterPill
            key={r}
            label={REVIEW_LABELS.find((o) => o.value === r)?.label ?? r}
            onRemove={() =>
              setSearch({
                review: reviewFilter.filter((v) => v !== r).join(",") || undefined,
              })
            }
          />
        ))}
        {(reviewFilter.length > 0 || q) && (
          <FilterClearAll onClick={() => setSearch({ review: undefined, q: undefined })} />
        )}
      </FilterBar>

      {canAct && (
        <BulkActionBar
          count={rows.length}
          totalCount={contracts.length}
          noun="contract"
          nounPlural="contracts"
          actions={[
            {
              label: "Approve",
              tone: "success",
              icon: <Check className="h-3.5 w-3.5" />,
              disabled: review.isPending,
              onClick: () => bulkReview(rows, "approved"),
            },
            {
              label: "Flag",
              tone: "warning",
              icon: <Flag className="h-3.5 w-3.5" />,
              disabled: review.isPending,
              onClick: () => bulkReview(rows, "flagged"),
            },
          ]}
        />
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        sort={sort}
        onSortChange={setSort}
        loading={isLoading}
        error={error ? String(error) : null}
        emptyMessage={
          contracts.length
            ? "No contracts match."
            : `No contracts were signed in ${curLabel}.`
        }
        onRowClick={(c) => openDrawer(c, false)}
        selectedKey={search.contract ?? null}
        containerClassName="max-h-[calc(100vh-16rem)]"
        footer={() => (
          <TableRow>
            <TableCell className="text-xs font-semibold">Total</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {rows.length} contract{rows.length === 1 ? "" : "s"}
            </TableCell>
            <TableCell />
            <TableCell numeric className="font-semibold">
              {fmtCur2(shownMrr)}
            </TableCell>
            <TableCell numeric className="font-semibold">
              {fmtCur(shownMrr * 12)}
            </TableCell>
            <TableCell colSpan={6} />
          </TableRow>
        )}
      />

      <p className="text-xs text-muted-foreground">
        Click a company to see its line items, PandaDoc and HubSpot deal, and to
        record a verdict. Fix anything wrong in Cinch and it updates here — no
        re-pull, though a contract that changes after review goes back to
        pending. Completing the step freezes this list as the record.
      </p>

      <SignedContractDrawer
        period={period}
        contract={drawerContract}
        canAct={canAct}
        expanded={!!search.expanded}
        presetFlag={flagIntent}
        onExpandedChange={(v) => setSearch({ expanded: v || undefined })}
        onClose={() => {
          setFlagIntent(false);
          setSearch({ contract: undefined, expanded: undefined });
        }}
      />
    </div>
  );

  /** Applies to exactly the rows on screen — `rows` is the filtered set, so
   *  scope and screen agree by construction. Sequential rather than a bulk
   *  endpoint: R-14 months are tens of contracts, not thousands. */
  async function bulkReview(target: SignedContract[], status: ReviewStatus) {
    const todo = target.filter((c) => c.review_status !== status);
    if (todo.length === 0) {
      toast.info(`Nothing to ${status === "approved" ? "approve" : "flag"}.`);
      return;
    }
    try {
      for (const c of todo) {
        await review.mutateAsync({ contractId: c.id, status });
      }
      toast.success(
        `${status === "approved" ? "Approved" : "Flagged"} ${todo.length} contract${todo.length === 1 ? "" : "s"}`,
      );
    } catch (e) {
      toast.error("Bulk review failed", { description: (e as Error).message });
    }
  }
}

function value(c: SignedContract, key: string): string | number | null {
  switch (key) {
    case "company_name":
      return c.company_name?.toLowerCase() ?? null;
    case "company_dba":
      return c.company_dba?.toLowerCase() ?? null;
    case "contract_type_name":
      return c.contract_type_name?.toLowerCase() ?? null;
    case "mrr_total":
    case "arr":
      return c.mrr_total;
    case "term_months":
      return c.term_months;
    case "payment_type":
      return c.payment_type?.toLowerCase() ?? null;
    case "sign_date":
      return c.sign_date;
    case "billing_effective_from":
      return c.billing_effective_from;
    case "closer_name":
      return c.closer_name?.toLowerCase() ?? null;
    case "review":
      return c.review_status;
    default:
      return null;
  }
}
